/**
 * Mindmap tree layout (pure — no DOM, no Store).
 *
 * Positions are recomputed on every render from the visible tree and the
 * nodes' ACTUAL sizes, so they can't go stale:
 *   - Every node owns a vertical band as tall as max(its own height, its
 *     stacked children). Sibling bands are stacked with gaps and never
 *     intersect, so no two subtrees can overlap.
 *   - Children sit beyond their parent's outer edge (half widths + gap), so a
 *     wide parent never covers its children.
 *   - The only per-node inputs are user choices: the side of the root's
 *     children (mmSide) and extra horizontal distance a user dragged in.
 *
 * Earlier versions persisted computed offsets and replayed them later, which
 * made nodes overlap after subtrees grew or were expanded, and jump between
 * the stored and the recomputed layout (see docs/architecture/frontend.md).
 */

/** Clearance between a parent's edge and its children's edges (px). */
export const H_GAP = 60;

/** Pre-2026-09-24 default centre-to-centre offsets (level 0 / deeper). */
export const LEGACY_OFFSET_LEVEL0 = 160;
export const LEGACY_OFFSET_DEEPER = 200;

/**
 * Extra horizontal distance a user dragged in, from a stored centre-to-centre
 * offset (mmOffsetX). Offsets at or under the old default mean "no extra".
 * @param {number|undefined} mmOffsetX
 * @param {number} level - 0 for children of the displayed root
 */
export function extraFromOffset(mmOffsetX, level) {
  if (!Number.isFinite(mmOffsetX)) return 0;
  const base = level === 0 ? LEGACY_OFFSET_LEVEL0 : LEGACY_OFFSET_DEEPER;
  return Math.max(0, Math.abs(mmOffsetX) - base);
}

/**
 * Inverse of extraFromOffset: what to store after a user drags a node so that
 * `extra` px separate it from the minimum clearance position.
 */
export function offsetForExtra(extra, level, side) {
  const base = level === 0 ? LEGACY_OFFSET_LEVEL0 : LEGACY_OFFSET_DEEPER;
  return (side === "left" ? -1 : 1) * (base + Math.max(0, Math.round(extra)));
}

/**
 * Side of each of the displayed root's children.
 *
 * balanced: a stored side (mmSide, set when a user drags a branch across)
 * wins; otherwise the first half of ALL children (hidden ones included) goes
 * right and the rest left. Depending only on the order and count keeps it
 * stable: expanding, collapsing, hiding or growing branches never moves one
 * across, pinning a side never moves another branch, and adding or removing
 * a branch moves at most the one at the middle.
 * right / left: every branch on that side (display preference, not stored).
 *
 * @param {{id: string, mmSide?: string}[]} children - all children, in order
 * @param {"balanced"|"right"|"left"} [mode]
 * @param {(node: object) => (string|undefined)} [sideOf] - stored side
 * @returns {Map<string, "left"|"right">}
 */
export function assignSides(children, mode = "balanced", sideOf = (node) => node.mmSide) {
  const sides = new Map();
  const half = Math.ceil(children.length / 2);
  children.forEach((child, i) => {
    const stored = sideOf(child);
    let side;
    if (mode === "right" || mode === "left") side = mode;
    else if (stored === "left" || stored === "right") side = stored;
    else side = i < half ? "right" : "left";
    sides.set(child.id, side);
  });
  return sides;
}

/**
 * @template T
 * @param {T & {id: string}} root
 * @param {object} opts
 * @param {(node: T) => {w: number, h: number}} opts.size - rendered size
 * @param {(node: T) => T[]} opts.children - VISIBLE children in order
 * @param {(node: T) => ("left"|"right")} opts.sideOf - side of a root child
 * @param {number} opts.vGap - gap between sibling bands
 * @param {number} [opts.subtreeGap=0] - extra gap next to siblings showing children
 * @param {number} [opts.hGap=H_GAP]
 * @param {(node: T, level: number) => number} [opts.extraX] - user-dragged extra distance
 * @returns {Map<string, {x: number, y: number, w: number, h: number, side: string|null, parentId: string|null, level: number}>}
 *   centres relative to the root centre
 */
export function computeTreeLayout(root, opts) {
  const { size, children, sideOf, vGap, subtreeGap = 0, hGap = H_GAP, extraX = () => 0 } = opts;
  const sizes = new Map();
  const sizeOf = (node) => {
    let s = sizes.get(node);
    if (!s) { s = size(node); sizes.set(node, s); }
    return s;
  };
  const kidsMemo = new Map();
  const kids = (node) => {
    let k = kidsMemo.get(node);
    if (!k) { k = children(node) || []; kidsMemo.set(node, k); }
    return k;
  };
  const gapBetween = (a, b) => vGap + (kids(a).length || kids(b).length ? subtreeGap : 0);
  const extentMemo = new Map();
  const stackHeight = (list) => list.reduce(
    (sum, node, i) => sum + extent(node) + (i ? gapBetween(list[i - 1], node) : 0), 0);
  function extent(node) {
    let e = extentMemo.get(node);
    if (e === undefined) {
      const list = kids(node);
      e = list.length ? Math.max(sizeOf(node).h, stackHeight(list)) : sizeOf(node).h;
      extentMemo.set(node, e);
    }
    return e;
  }

  const positions = new Map();
  const rootSize = sizeOf(root);
  positions.set(root.id, { x: 0, y: 0, w: rootSize.w, h: rootSize.h, side: null, parentId: null, level: -1 });

  function place(parent, px, py, list, sign, level) {
    const parentW = sizeOf(parent).w;
    let top = py - stackHeight(list) / 2;
    list.forEach((node, i) => {
      if (i) top += gapBetween(list[i - 1], node);
      const { w, h } = sizeOf(node);
      const extra = Math.max(0, extraX(node, level) || 0);
      const x = px + sign * (parentW / 2 + hGap + w / 2 + extra);
      const band = extent(node);
      const y = top + band / 2;
      positions.set(node.id, { x, y, w, h, side: sign > 0 ? "right" : "left", parentId: parent.id, level });
      place(node, x, y, kids(node), sign, level + 1);
      top += band;
    });
  }

  const rootKids = kids(root);
  place(root, 0, 0, rootKids.filter((c) => sideOf(c) !== "left"), 1, 0);
  place(root, 0, 0, rootKids.filter((c) => sideOf(c) === "left"), -1, 0);
  return positions;
}
